import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getPrinters, deletePrinter } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SearchInput from "../components/UI/SearchInput";
import ConfirmModal from "../components/UI/ConfirmModal";
import { notify } from "../utils/notify";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

// PRINTER_STATUS_BADGE mirrors models.Printer's own LastStatus constants
// (PrinterStatusOnline/Offline/Unknown in printer.go) — a best-effort
// connectivity report from the owning agent's own poll, not read live from
// this screen (see that model's own doc comment).
const PRINTER_STATUS_BADGE = {
  online: "bg-green-50 text-green-600",
  offline: "bg-red-50 text-red-500",
  unknown: "bg-gray-100 text-gray-500",
};

export default function Printers() {
  const { t } = useTranslation("printers");
  const { t: tCommon } = useTranslation("common");
  const [printers, setPrinters] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  const [modalConfig, setModalConfig] = useState({ isOpen: false, id: null });
  const [status, setStatus] = useState("idle");

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchPrinters = async () => {
    try {
      const data = await getPrinters(token);
      setPrinters(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchPrinters(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedAndFiltered = useMemo(() => {
    let result = [...printers].filter((p) =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.host?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [printers, searchTerm, sortConfig]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const handleDeleteClick = (id) => setModalConfig({ isOpen: true, id });

  const confirmDelete = async () => {
    const printerID = modalConfig.id;
    try {
      setStatus("loading");
      await deletePrinter(token, printerID);
      setStatus("success");
      setTimeout(() => {
        setModalConfig({ isOpen: false, id: null });
        setStatus("idle");
      }, 1200);
      notify.success(t("notify_deleted", "Printer deleted successfully"));
      fetchPrinters();
    } catch (err) {
      setStatus("error");
      const serverMessage = err.data?.error || err.message;
      // This exact string is DeletePrinterHandler's own 409 message (see
      // its doc comment on the backend) — the one case where the delete
      // failed for a reason the admin can actually fix themselves, right
      // now, one click away, instead of a dead end. Every other failure
      // (network error, permissions, printer already gone) just gets the
      // plain message, same as before.
      if (serverMessage === "Cannot delete a printer that still has print jobs on record") {
        notify.error(
          <span>
            {t("notify_delete_blocked_by_jobs", "This printer still has print jobs on record.")}{" "}
            <button
              type="button"
              className="underline font-bold"
              onClick={() => navigate(`/print-jobs?printer_id=${printerID}`)}
            >
              {t("notify_delete_blocked_link", "View & clear its print jobs")}
            </button>
          </span>
        );
      } else {
        notify.error(serverMessage || t("notify_delete_failed", "Failed to delete printer"));
      }
    }
  };

  const columns = [
    { key: "id", label: tCommon("table_col_id", "ID"), className: "text-gray-400 font-mono" },
    {
      key: "name",
      label: t("col_name", "Printer"),
      render: (row) => (
        <div className="flex items-center gap-3 font-bold">
          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
            <ICONS.Printers className="w-4 h-4" />
          </div>
          <div>{row.name}</div>
        </div>
      ),
    },
    { key: "agent", label: t("col_agent", "Print Agent"), className: "text-gray-600", render: (row) => row.print_agent?.name || "—" },
    { key: "branch", label: t("col_branch", "Branch"), className: "text-gray-600", render: (row) => row.print_agent?.branch?.name || "—" },
    { key: "host", label: t("col_address", "Address"), className: "text-gray-600 font-mono", render: (row) => `${row.host}:${row.port}` },
    {
      key: "is_default",
      label: t("col_default", "Default"),
      render: (row) => row.is_default ? (
        <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-blue-50 text-blue-600">{tCommon("btn_yes", "Yes")}</span>
      ) : (
        <span className="text-gray-300">—</span>
      ),
    },
    {
      key: "last_status",
      label: tCommon("table_col_status", "Status"),
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${PRINTER_STATUS_BADGE[row.last_status] || PRINTER_STATUS_BADGE.unknown}`}>
          {t(`status_${row.last_status || "unknown"}`, row.last_status || "unknown")}
        </span>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title={t("delete_confirm_title", "Delete Printer")}
        message={t("delete_confirm_message", "Are you sure? This will fail if the printer still has print jobs on record.")}
        onConfirm={confirmDelete}
        onCancel={() => setModalConfig({ isOpen: false, id: null })}
        isLoading={status === "loading"}
        isSuccess={status === "success"}
        isError={status === "error"}
      />

      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.Printers size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Printers")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "Network thermal printers reachable through a Print Agent")}</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          <SearchInput
            value={searchTerm}
            onChange={(val) => setSearchTerm(val)}
            placeholder={t("search_placeholder", "Search printers...")}
          />
          <Button variant="primary" icon={ICONS.Plus} onClick={() => navigate("/add-printer")} className="w-full sm:w-auto shrink-0">
            {t("btn_add_printer", "Add Printer")}
          </Button>
        </div>
      </PageHeader>

      <DataTable
        columns={columns}
        data={sortedAndFiltered}
        sortConfig={sortConfig}
        onSort={requestSort}
        renderActions={(row, isMobile) => (
          <>
            <Button variant="outline" size="sm" icon={ICONS.EditUser} onClick={() => navigate(`/edit-printer/${row.id}`)}>{isMobile && ` ${tCommon("btn_edit", "Edit")}`}</Button>
            <Button variant="danger" size="sm" icon={ICONS.DeleteUser} onClick={() => handleDeleteClick(row.id)}>{isMobile && ` ${tCommon("btn_delete", "Delete")}`}</Button>
          </>
        )}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400 font-mono">{row.host}:{row.port}</span>
            <h3 className="font-bold text-gray-800">{row.name}</h3>
          </div>
        )}
      />
    </div>
  );
}
